import {
  Column,
  Columns,
  ContentBlock,
  Heading,
  IconSearch,
  Stack,
  TextField,
} from 'braid-design-system';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  MINIMUM_CHARACTERS_SEARCH,
  PageLoader,
  useAppContext,
  useConfig,
} from '@seek/libs-shared';
import { useTranslations } from '@vocab/react';

import { useService } from '../../../shared/providers/serviceProvider';
import { trackViewEvents } from '../../../shared/tracking/candidateApp/trackViewEvents';
import type { SearchResults } from '../../../shared/services/companyProfileService/types';

import { MainLayout } from '../../layouts/mainLayout/MainLayout';
import { NoResults } from '../../components/noResults/NoResults';

import translations from './.vocab';
import { CompanySearchCard } from '../../components/companySearchCard/CompanySearchCard';
import { useMelwaysLink } from '@seek/melways-react';
import { ROOT_PATH } from '../../../paths';

export const SearchPage = () => {
  const { t } = useTranslations(translations);
  const { companyProfileService } = useService();
  const config = useConfig();
  const context = useAppContext();

  const [searchParams] = useSearchParams();
  const [searchCompaniesResult, setSearchCompaniesResult] =
    useState<SearchResults>();
  const [searchIsLoading, setSearchIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get('term')?.toString() || '',
  );

  const navigate = useNavigate();
  const { language } = useConfig();
  const localize = useMelwaysLink();

  useEffect(() => {
    setSearchTerm(searchParams.get('term')?.toString() || '');
    setSearchCompaniesResult(undefined);
    setSearchIsLoading(true);
  }, [searchParams]);

  useEffect(() => {
    setSearchCompaniesResult(undefined);
    if (searchTerm && searchTerm.length >= MINIMUM_CHARACTERS_SEARCH) {
      setSearchIsLoading(true);
      companyProfileService
        .searchCompanyProfile({ name: searchTerm, zone: config.zone })
        .then((companies) => {
          setSearchCompaniesResult(companies);
          window.scroll({ top: 0, behavior: 'smooth' });
          setSearchIsLoading(false);
          trackViewEvents.searchResultsPage({
            config,
            context,
            searchKeyword: searchTerm,
            companyCount: companies.paging.total,
          });
        });
    } else {
      setSearchIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyProfileService, searchTerm]);

  return (
    <MainLayout>
      <ContentBlock width="small">
        <Stack space="xlarge">
          <Heading level="3">
            {t("Search results for 'searchTerm'", { searchTerm })}
          </Heading>
          <Columns space="small" collapseBelow="tablet">
            <Column>
              <TextField
                id="company-search-bar"
                placeholder={t('Search by company name')}
                icon={<IconSearch />}
                aria-label={t('Search by company name')}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.currentTarget.value);
                }}
              />
            </Column>
          </Columns>

          {searchIsLoading ? (
            <PageLoader />
          ) : (
            <Stack space="small">
              {searchCompaniesResult?.paging.total ? (
                searchCompaniesResult.data.map((company) => (
                  <CompanySearchCard
                    key={company.slug}
                    name={company.name}
                    logo={company.logoUrl}
                    averageCompanyRating={company?.overallRating?.rating}
                    reviewsCount={company?.overallRating?.count}
                    onClick={() => {
                      window.scroll(0, 0);
                      navigate(
                        `${localize({
                          path: `/${ROOT_PATH}/${company.slug}`,
                          language,
                        })}`,
                      );
                    }}
                  />
                ))
              ) : (
                <NoResults />
              )}
            </Stack>
          )}
        </Stack>
      </ContentBlock>
    </MainLayout>
  );
};
